import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { utils, i18next, site, dialogs, linkEditing, view } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import segment from 'src/js/modules/analytics/segment';
import blocks from 'src/js/modules/blocks';
import errorCodes from 'src/js/modules/extensions/common/constants/error-codes';
import getItemClickAction from 'src/js/modules/extensions/common/helpers/get-item-click-action';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import { LINK } from 'src/js/modules/bindings/constants/element-types';
import LinkSettingDialog from '../components/link-setting-dialog';
import isOpenAddress from '../verifiers/is-open-address';
import constants from '../constants';
import helpers from '../helpers';
import useCustomPathState from '../hooks/use-custom-path-state';

const { useForm, useWatch, FormProvider } = reactHookForm;

const {
  trackers: { trackEvent, trackLinkSettingsInputClicked },
  constants: { events },
} = segment;

const {
  validation: { isURL, isFullyURL, isPhoneNumber, isEmail },
  forceHTTPS,
} = utils;

const {
  linkSettings: { DIALOG_WIDTH, DIALOG_HEIGHT, INPUTS_IDS, DOWNLOAD_OPTIONS_VALUES, FIELD_NAMES },
} = constants;

const { BLANK, NOFOLLOW } = linkEditing.constants.common;
const { linkTypes } = linkEditing.constants;

const { getLinkTypeOptions, sortAlphabetically, getDefaultLinkTypes } = helpers;
const pathRegexp = /^\/(?!ws\/)([^\s?^/#]+\/?)+[^/]$/;
const hashRouteRegexp = /^#(?!#)[^\s]*$/;

const flattenOptions = (options) => options.flatMap((option) => option?.options || option);

const getInitialReference = (reference, linkType, blocksOptions) => {
  if (
    linkType === linkTypes.ANCHOR &&
    !flattenOptions(blocksOptions).find(({ value }) => value === reference)
  ) {
    return '';
  }

  return reference;
};

const defaultPropElement = {};
const defaultModalAttributes = {};
const defaultAllowedLinkTypes = [];

const LinkSettingContainer = ({
  currentConfig = null,
  initialConfig,
  initialTitle = '',
  offsetX = null,
  offsetY = null,
  centered = null,
  element = defaultPropElement,
  sourceDialog,
  allowedLinkTypes = defaultAllowedLinkTypes,
  appearance = constants.common.DIALOG_APPEARANCE,
  modalAttributes = defaultModalAttributes,
  showDeleteButton = false,
  handleDeleteLink = utils.noop,
  setIsOverlayClicked = utils.noop,
  handleSubmit = utils.noop,
  handleCancel = utils.noop,
  handleChange = utils.noop,
  ...rest
}) => {
  const [featureFlags] = useFeatureFlags(['yola_cms']);
  const isBindingsEnabled = Boolean(featureFlags.yola_cms);

  const activePageId = useSelector(site.selectors.getActivePageId);
  const activePage = useSelector((state) => site.selectors.getPage(state, activePageId));
  const pages = useSelector(site.selectors.getPages);
  const pagesOptions = useMemo(
    () =>
      pages
        .map(({ id, name, locale }) => ({ value: id, label: name, locale }))
        .sort((a, b) => sortAlphabetically(a.label, b.label)),
    [pages]
  );

  const currentPageBlocksOptions = useMemo(() => blocks.helpers.getBlocksReadableNames(), []);

  const blocksOptions = useMemo(() => {
    let options = currentPageBlocksOptions;

    if (initialConfig.linkType === linkTypes.ANCHOR) {
      const { pageId: initialPageId } = initialConfig;

      if (activePageId !== initialPageId) {
        const matchedPage = pages.find((page) => page.id === initialPageId);
        if (matchedPage) {
          options = [
            {
              label: matchedPage.name,
              options: [
                {
                  label: `#${initialConfig.reference}`,
                  value: initialConfig.reference,
                  pageId: initialPageId,
                },
              ],
            },
            {
              label: activePage.name,
              options: currentPageBlocksOptions,
            },
          ];
        }
      }
    }

    return options;
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const isDialog = appearance === constants.common.DIALOG_APPEARANCE;

  const dialogPosition = useMemo(() => {
    if (!isDialog) return null;

    return !centered && (offsetX === null || offsetY === null)
      ? dialogs.helpers.getPositionByElement(element, DIALOG_WIDTH, DIALOG_HEIGHT)
      : { offsetX, offsetY };
  }, [offsetX, offsetY, element, centered, isDialog]);

  const cfg = currentConfig || initialConfig;

  let initialLinkType = currentConfig?.linkType;
  let initialReference = getInitialReference(
    currentConfig?.reference,
    initialLinkType,
    blocksOptions
  );

  if (!initialLinkType) {
    const tmpConfig = helpers.resolveInitialConfig(initialConfig);
    initialLinkType = tmpConfig.linkType;
    initialReference = getInitialReference(tmpConfig.reference, initialLinkType, blocksOptions);
  }

  const initialAnchorReference = initialLinkType === linkTypes.ANCHOR ? initialReference : '';

  const [latestAnchorReference, setLatestAnchorReference] = useState(initialAnchorReference);
  const [pageId, setPageId] = useState(cfg.pageId);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(cfg.download);

  const onDownloadUrlToggle = useCallback(() => {
    trackEvent(events.LINK_SETTINGS_OPTION_CLICKED, {
      dialogId: sourceDialog.id,
      optionId: 'set-download-url',
      newTabAdjusted: null,
    });
  }, [sourceDialog.id]);

  const {
    state: customPathState,
    setCustomPath,
    toggleCustomPathField,
    enableCustomPath,
  } = useCustomPathState(initialLinkType, initialReference, {
    onDownloadUrlToggle,
  });

  const defaultDownloadOption =
    initialLinkType === linkTypes.DOWNLOAD && cfg.target
      ? DOWNLOAD_OPTIONS_VALUES.openInNewTab
      : DOWNLOAD_OPTIONS_VALUES.downloadOnComputer;
  const defaultNofollowControl = cfg.rel ? cfg.rel === NOFOLLOW : false;
  const defaultTargetBlank =
    initialLinkType !== linkTypes.DOWNLOAD && cfg.target ? cfg.target === BLANK : false;

  const initialValues = {
    [FIELD_NAMES.title]: initialTitle,
    [FIELD_NAMES.reference]: initialReference,
    [FIELD_NAMES.linkType]: initialLinkType,
    [FIELD_NAMES.customPath]: customPathState.value,
    [FIELD_NAMES.customPathSwitcher]: customPathState.visible,
    [FIELD_NAMES.download]: defaultDownloadOption,
    [FIELD_NAMES.nofollowControl]: defaultNofollowControl,
    [FIELD_NAMES.targetBlank]: defaultTargetBlank,
  };

  const methods = useForm({
    defaultValues: initialValues,
    mode: 'onTouched',
  });

  const {
    control,
    trigger,
    setValue,
    formState: { errors },
  } = methods;

  const [reference, linkType, downloadOption, nofollowControl, targetBlank] = useWatch({
    control,
    name: [
      FIELD_NAMES.reference,
      FIELD_NAMES.linkType,
      FIELD_NAMES.download,
      FIELD_NAMES.nofollowControl,
      FIELD_NAMES.targetBlank,
    ],
  });

  useEffect(() => {
    setValue(FIELD_NAMES.customPathSwitcher, customPathState.visible);
    setValue(FIELD_NAMES.customPath, customPathState.value);
  }, [setValue, customPathState]);

  const linkSelectorOptions = useMemo(() => {
    const allowedTypes = allowedLinkTypes.length !== 0 ? allowedLinkTypes : getDefaultLinkTypes();

    return {
      pagesOptions,
      blocksOptions,
      linkTypeOptions: getLinkTypeOptions(allowedTypes, isBindingsEnabled),
    };
  }, [allowedLinkTypes, pagesOptions, blocksOptions, isBindingsEnabled]);

  const linkSelectorCaptions = useMemo(
    () => ({
      fileUploadCaptions: {
        button: i18next.t('Choose file'),
        empty: i18next.t('No file chosen'),
        fileItemName: fileName,
      },
      pagesCaptions: {
        noResultsText: i18next.t('No results found'),
      },
      blocksCaptions: {
        placeholder: i18next.t('Select block...'),
        noResultsText: i18next.t('No results found'),
      },
    }),
    [fileName]
  );

  const getChangePayload = () => {
    const config = {
      linkType,
      reference,
    };

    if (linkType === linkTypes.DOWNLOAD) {
      if (file) config.file = file;

      config.download = fileName;

      if (downloadOption === DOWNLOAD_OPTIONS_VALUES.openInNewTab) {
        config.target = BLANK;
      }

      if (customPathState.visible && customPathState.value && customPathState.value.trim()) {
        config.customPath = customPathState.value.trim();
      }
    }

    if (linkType === linkTypes.ANCHOR) {
      config.pageId = pageId;
    }

    if (linkType === linkTypes.PHONE || linkType === linkTypes.MAIL) {
      config.target = BLANK;
    }

    if (linkType === linkTypes.PAGE) {
      const page = site.accessors.getPages().find(({ id }) => id === reference);
      const isHome = site.verifiers.isHomePage(page.path);
      if (isHome) {
        const tmpConfig = {
          linkType: linkTypes.PATH,
          reference: '/',
        };

        if (page.locale !== activePage.locale) {
          tmpConfig.locale = page.locale;
        }

        return tmpConfig;
      }
    }

    if (isOpenAddress(linkType)) {
      const trimmedReference = reference.trim();
      if (isURL(trimmedReference)) {
        config.linkType = linkTypes.EXTERNAL;
        config.reference = forceHTTPS(trimmedReference);
      } else {
        config.linkType = linkTypes.PATH;
        config.reference = trimmedReference;
      }

      if (nofollowControl) {
        config.rel = NOFOLLOW;
      }

      if (targetBlank) {
        config.target = BLANK;
      }
    }

    return config;
  };

  useEffect(
    () => {
      if (!isDialog) {
        handleChange(getChangePayload(), {
          trigger,
          errors,
        });
      }
    },
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    [
      linkType,
      reference,
      targetBlank,
      nofollowControl,
      fileName,
      downloadOption,
      customPathState.visible,
      customPathState.value,
      errors,
    ]
  );

  const onLinkChange = (data) => {
    const { linkType: newLinkType, reference: newReference, pageId: newPageId } = data;

    if (newLinkType === 'binding') {
      const linkElementId = view.accessors.getLiveElementId(element);
      dialogs.operations.show(dialogTypes.CONNECT_TO_CMS_DIALOG, {
        elementId: linkElementId,
        elementType: LINK,
      });
      return;
    }

    if (newLinkType === linkTypes.ANCHOR) {
      setLatestAnchorReference(newReference);
    }

    if (newLinkType === linkTypes.DOWNLOAD) {
      setFile(null);
      setFileName('');
      enableCustomPath();
    }

    setPageId(newPageId);
  };

  const validatePath = (value) => {
    if (!value) {
      return i18next.t('Invalid URL. Please, check the URL and try again');
    }

    if (!value.startsWith('/')) {
      return i18next.t('The "/" is missing at the beginning');
    }

    if (value.endsWith('/')) {
      return i18next.t('URL can\'t end with "/". Please remove it');
    }

    if (!pathRegexp.test(value)) {
      return i18next.t('Invalid URL. Please, check the URL and try again');
    }

    return null;
  };

  const validateLink = (newReference, formValues) => {
    const { PHONE, MAIL, PATH, DOWNLOAD, EXTERNAL, ANCHOR } = linkTypes;

    const isMakePhoneCall = formValues.linkType === PHONE;
    const isSendEmail = formValues.linkType === MAIL;
    const isDownloadFile = formValues.linkType === DOWNLOAD;
    const isOpenAddres = isOpenAddress(formValues.linkType);
    const isAnchor = formValues.linkType === ANCHOR;

    if (isMakePhoneCall) {
      if (!newReference)
        return i18next.t('Invalid phone number. The phone number can not be empty');
      if (!isPhoneNumber(newReference.trim()))
        return i18next.t('Invalid phone number. Please, check the phone number and try again');
    }

    if (isSendEmail) {
      if (!newReference) return i18next.t('Invalid email. The email can not be empty');
      if (!isEmail(newReference.trim()))
        return i18next.t('Invalid email. Please, check the email and try again');
    }

    if (isAnchor) {
      if (!newReference) return i18next.t('Please select some block');
    }

    if (!isOpenAddres && !isDownloadFile) return null;

    if (isDownloadFile && !fileName) {
      return i18next.t('You need to attach a file');
    }

    if (!isOpenAddres) return null;

    const val = newReference.trim();

    if (!val) {
      return i18next.t('Invalid URL. The URL can not be empty');
    }

    if (val.startsWith('#')) {
      if (!hashRouteRegexp.test(val)) {
        return i18next.t('Invalid URL. Please, check the URL and try again');
      }

      return null;
    }

    const type = val.length > 3 && isURL(val) ? EXTERNAL : PATH;

    if (type === PATH) {
      const tmpURL = `https://yola.com${val}`;
      if (!val.startsWith('/') || !isURL(tmpURL)) {
        return i18next.t('Invalid URL. Please, check the URL and try again');
      }
    } else if (!isFullyURL(val)) {
      return i18next.t('Invalid URL. Please, check the URL and try again');
    }

    return null;
  };

  const handleUploadFile = useCallback(
    (newFile) => {
      setFile(newFile);
      setFileName(newFile.name);
      enableCustomPath();
    },
    [enableCustomPath]
  );

  const onSubmit = (newOffsetX, newOffsetY, data) => {
    if (!isDialog) return;

    const configData = getChangePayload();
    const { title } = data;

    helpers
      .processLinkSubmit({
        config: configData,
      })
      .then((newConfig) => {
        handleSubmit({
          customPath: customPathState.value || null,
          setDownloadUrlAdjusted: linkTypes.DOWNLOAD === linkType ? customPathState.visible : null,
          config: newConfig,
          title,
          offsetX: newOffsetX,
          offsetY: newOffsetY,
        });
      })
      .catch((error) => {
        if (error === errorCodes.UPLOAD_FILE_CANCELED) {
          handleCancel({ offsetX: newOffsetX, offsetY: newOffsetY });
          return;
        }

        console.error(error);
      });
  };

  const handleTargetBlankChange = (checked) => {
    trackEvent(events.LINK_SETTINGS_OPTION_CLICKED, {
      dialogId: sourceDialog.id,
      newTabAdjusted: checked,
      optionId: 'open-in-new-tab',
    });
  };

  const handleDownloadOptionChange = (value) => {
    trackEvent(events.LINK_SETTINGS_OPTION_CLICKED, {
      dialogId: sourceDialog.id,
      optionId: value,
      newTabAdjusted: null,
    });
  };

  const handleNoFollowChange = () => {
    trackEvent(events.LINK_SETTINGS_OPTION_CLICKED, {
      dialogId: sourceDialog.id,
      optionId: 'add-no-follow-tag',
      newTabAdjusted: null,
    });
  };

  const handleInputClick = (inputId, newLinkType) => {
    if (inputId === INPUTS_IDS.downloadInput) {
      trackEvent(events.LINK_SETTINGS_CHOOSE_FILE_CLICKED, {
        dialogId: sourceDialog.id,
      });
      return;
    }

    const isActionTypeSelectClicked = inputId === INPUTS_IDS.actionTypeInput;

    const traits = {
      dialogId: sourceDialog.id,
      inputId: isActionTypeSelectClicked ? null : inputId,
      actionType: isActionTypeSelectClicked ? getItemClickAction(newLinkType) : null,
    };

    trackLinkSettingsInputClicked(traits);
  };

  return (
    <FormProvider {...methods}>
      <LinkSettingDialog
        latestAnchorReference={latestAnchorReference}
        linkSelectorOptions={linkSelectorOptions}
        linkSelectorCaptions={linkSelectorCaptions}
        handleDownloadOption={handleDownloadOptionChange}
        handleLinkChange={onLinkChange}
        validateLink={validateLink}
        handleSubmit={onSubmit}
        handleCancel={handleCancel}
        handleTargetBlankChange={handleTargetBlankChange}
        handleNoFollowChange={handleNoFollowChange}
        handleUploadFile={handleUploadFile}
        dialogPosition={dialogPosition}
        centered={centered}
        appearance={appearance}
        onInputClick={handleInputClick}
        customPathDisabled={customPathState.disabled}
        customPathIsLoading={customPathState.isLoading}
        pathValidation={validatePath}
        toggleCustomPathField={toggleCustomPathField}
        onCustomPathChange={setCustomPath}
        modalAttributes={modalAttributes}
        showDeleteButton={showDeleteButton}
        handleDeleteLink={handleDeleteLink}
        setIsOverlayClicked={setIsOverlayClicked}
        {...rest}
      />
    </FormProvider>
  );
};

LinkSettingContainer.propTypes = {
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  captions: PropTypes.shape().isRequired,
  initialConfig: PropTypes.shape({
    linkType: PropTypes.string.isRequired,
    reference: PropTypes.string,
    download: PropTypes.string,
    target: PropTypes.string,
    rel: PropTypes.string,
    pageId: PropTypes.string,
  }).isRequired,
  currentConfig: PropTypes.shape({
    linkType: PropTypes.string.isRequired,
    reference: PropTypes.string,
    download: PropTypes.string,
    target: PropTypes.string,
    rel: PropTypes.string,
    pageId: PropTypes.string,
  }),
  initialTitle: PropTypes.string,
  modalAttributes: PropTypes.shape({}),
  handleSubmit: PropTypes.func,
  handleCancel: PropTypes.func,
  handleChange: PropTypes.func,
  showDeleteButton: PropTypes.bool,
  handleDeleteLink: PropTypes.func,
  element: PropTypes.object,
  setIsOverlayClicked: PropTypes.func,
  sourceDialog: PropTypes.shape({
    id: PropTypes.string.isRequired,
    props: PropTypes.shape({}),
  }).isRequired,
  allowedLinkTypes: PropTypes.array,
  centered: PropTypes.bool,
  appearance: PropTypes.oneOf([
    constants.common.DIALOG_APPEARANCE,
    constants.common.DIALOG_BLOCK_APPEARANCE,
  ]),
};

export default LinkSettingContainer;
